import React, { useEffect, useRef, useState } from 'react';
import { MapPin, Search, Loader2 } from 'lucide-react';
import { inputCls, labelCls } from './smartqrApi';

const INDIAN_STATES = ['Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh', 'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu', 'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'];

const pick = (addr, keys) => keys.map((k) => addr[k]).find(Boolean) || '';
const norm = (v) => (v || '').trim().toLowerCase();
const uniq = (arr) => [...new Set(arr.filter(Boolean))];

// India Post public API (no key): post offices incl. villages / taluka-level places
const postOffices = async (name) => {
  try { const r = await fetch(`https://api.postalpincode.in/postoffice/${encodeURIComponent(name)}`); const d = await r.json(); return d?.[0]?.PostOffice || []; } catch { return []; }
};
const pinLookup = async (pin) => {
  try { const r = await fetch(`https://api.postalpincode.in/pincode/${pin}`); const d = await r.json(); return d?.[0]?.PostOffice || []; } catch { return []; }
};

export const LocationFields = ({ value, onChange, testPrefix = 'loc' }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [open, setOpen] = useState(false);
  const [districtSugg, setDistrictSugg] = useState([]);
  const [citySugg, setCitySugg] = useState([]);
  const auto = useRef({}); // which fields were auto-filled, and under which parent
  const timer = useRef(null);
  const selectedLabel = useRef('');
  const set = (patch) => onChange({ ...value, ...patch });
  const isIndia = norm(value.country || 'India') === 'india';

  // Top search: Nominatim (worldwide, towns/villages)
  useEffect(() => {
    if (query.trim().length < 3 || query === selectedLabel.current) { setResults([]); return; }
    clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      setSearching(true);
      try {
        const cc = isIndia ? '&countrycodes=in' : '';
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&addressdetails=1&limit=6${cc}`, { headers: { 'Accept-Language': 'en' } });
        setResults(await res.json()); setOpen(true);
      } catch { setResults([]); }
      setSearching(false);
    }, 450);
    return () => clearTimeout(timer.current);
  }, [query, isIndia]);

  // District suggestions: re-queried (OpenStreetMap) whenever the typed text or the selected state changes
  useEffect(() => {
    const q = (value.district || '').trim();
    if (q.length < 2) { setDistrictSugg([]); return; }
    let alive = true;
    const t = setTimeout(async () => {
      try {
        const cc = isIndia ? '&countrycodes=in' : '';
        const res = await fetch(`https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(`${q} district${value.state ? ', ' + value.state : ''}`)}&format=json&addressdetails=1&limit=8${cc}`, { headers: { 'Accept-Language': 'en' } });
        const rows = await res.json();
        const names = rows.filter((r) => !value.state || norm(r.address?.state) === norm(value.state))
          .map((r) => (pick(r.address || {}, ['state_district', 'district', 'county']) || r.name || '').replace(/ District$/i, ''));
        if (alive) setDistrictSugg(uniq(names));
      } catch { if (alive) setDistrictSugg([]); }
    }, 400);
    return () => { alive = false; clearTimeout(t); };
  }, [value.district, value.state, isIndia]);

  // City/Town/Village suggestions as the user types (filtered by state/district when set); auto-fill on exact match
  useEffect(() => {
    const q = (value.city || '').trim();
    if (!isIndia || q.length < 3) { setCitySugg([]); return; }
    const t = setTimeout(async () => {
      const offices = await postOffices(q);
      const filtered = offices.filter((o) => (!value.state || norm(o.State) === norm(value.state)) && (!value.district || norm(o.District) === norm(value.district))).slice(0, 12);
      setCitySugg(filtered);
      const match = filtered.find((o) => norm(o.Name) === norm(q));
      if (match && (!value.state || !value.district || !value.pincode)) {
        const patch = {};
        if (!value.state) patch.state = match.State;
        if (!value.district) { patch.district = match.District; auto.current.district = match.State; }
        if (!value.pincode) patch.pincode = match.Pincode;
        auto.current.city = match.District;
        onChange({ ...value, ...patch });
      }
    }, 400);
    return () => clearTimeout(t);
  }, [value.city, value.state, value.district, isIndia]); // eslint-disable-line react-hooks/exhaustive-deps

  const onState = (state) => {
    const patch = { state };
    if (auto.current.district && norm(auto.current.district) !== norm(state)) { patch.district = ''; patch.city = ''; auto.current.district = null; auto.current.city = null; }
    set(patch);
  };
  const onDistrict = (district) => {
    const patch = { district };
    if (auto.current.city && norm(auto.current.city) !== norm(district)) { patch.city = ''; auto.current.city = null; }
    set(patch);
  };
  const onCity = (city) => {
    const match = citySugg.find((o) => norm(o.Name) === norm(city));
    const patch = { city };
    if (match) {
      if (!value.state) patch.state = match.State;
      if (!value.district) { patch.district = match.District; auto.current.district = match.State; }
      if (!value.pincode) patch.pincode = match.Pincode;
      auto.current.city = match.District;
    }
    set(patch);
  };
  const onPin = async (raw) => {
    const pincode = raw.replace(/[^0-9A-Za-z -]/g, '').slice(0, 12);
    set({ pincode });
    if (isIndia && /^\d{6}$/.test(pincode)) {
      const offices = await pinLookup(pincode);
      if (offices.length) {
        const o = offices[0];
        const patch = { pincode };
        if (!value.state) patch.state = o.State;
        if (!value.district) { patch.district = o.District; auto.current.district = o.State; }
        if (!value.city) { patch.city = o.Block && o.Block !== 'NA' ? o.Block : o.Name; auto.current.city = o.District; }
        onChange({ ...value, ...patch });
      }
    }
  };
  const selectNominatim = async (r) => {
    const a = r.address || {};
    const country = a.country || value.country || 'India';
    let state = pick(a, ['state', 'region']) || value.state;
    let district = (pick(a, ['state_district', 'district', 'county']) || '').replace(/ District$/i, '');
    const town = pick(a, ['city', 'town', 'village', 'municipality', 'hamlet', 'suburb']) || r.name || value.city;
    const locality = (r.name && norm(r.name) !== norm(town) ? r.name : '') || pick(a, ['suburb', 'neighbourhood', 'quarter', 'hamlet', 'village']) || town;
    let pincode = a.postcode || '';
    setQuery(r.display_name); selectedLabel.current = r.display_name; setOpen(false);
    // Fill gaps (PIN / district / state) from India Post when OpenStreetMap lacks them
    if (norm(country) === 'india' && (!pincode || !district || !state)) {
      const offices = await postOffices(town);
      const o = offices.find((x) => (!state || norm(x.State) === norm(state)) && (!district || norm(x.District) === norm(district))) || offices[0];
      if (o) { pincode = pincode || o.Pincode; district = district || o.District; state = state || o.State; }
    }
    auto.current = { district: state, city: district };
    set({ country, state, district: district || value.district, city: town, pincode: pincode || value.pincode, area: locality || value.area, address: value.address || r.display_name });
  };

  return (
    <div className="space-y-3" data-testid={`${testPrefix}-fields`}>
      <div className="relative">
        <label className={labelCls}>Find your town / locality (type to search)</label>
        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={query} onChange={(e) => setQuery(e.target.value)} onFocus={() => results.length && setOpen(true)} onBlur={() => setTimeout(() => setOpen(false), 150)} className={`${inputCls} pl-9`} placeholder="e.g. Baramati, Karad, Kothrud Pune" data-testid={`${testPrefix}-search`} />
          {searching && <Loader2 className="w-4 h-4 text-orange-500 animate-spin absolute right-3 top-1/2 -translate-y-1/2" />}
        </div>
        {open && results.length > 0 && (
          <ul className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-xl shadow-lg max-h-60 overflow-y-auto" data-testid={`${testPrefix}-results`}>
            {results.map((r) => <li key={r.place_id}><button type="button" onMouseDown={() => selectNominatim(r)} className="w-full text-left px-3 py-2.5 text-xs text-slate-700 hover:bg-orange-50 flex gap-2" data-testid={`${testPrefix}-result`}><MapPin className="w-3.5 h-3.5 text-orange-500 shrink-0 mt-0.5" /><span>{r.display_name}</span></button></li>)}
          </ul>
        )}
        <p className="text-[11px] text-slate-400 mt-1">Suggestions come from OpenStreetMap and India Post (villages & taluka places included). You can always type values manually.</p>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div><label className={labelCls}>Country *</label>
          <select value={isIndia ? 'India' : 'Other'} onChange={(e) => { auto.current = {}; set({ country: e.target.value === 'India' ? 'India' : '', state: '', district: '', city: '', pincode: '' }); }} className={inputCls} data-testid={`${testPrefix}-country-select`}>
            <option value="India">India</option><option value="Other">Other</option>
          </select>
          {!isIndia && <input value={value.country || ''} onChange={(e) => set({ country: e.target.value })} className={`${inputCls} mt-2`} placeholder="Country name" data-testid={`${testPrefix}-country-input`} />}
        </div>
        <div><label className={labelCls}>State / Region *</label>
          <input list={isIndia ? `${testPrefix}-states` : undefined} value={value.state || ''} onChange={(e) => onState(e.target.value)} className={inputCls} placeholder={isIndia ? 'e.g. Maharashtra' : 'State / Region'} data-testid={`${testPrefix}-state-input`} />
          <datalist id={`${testPrefix}-states`}>{INDIAN_STATES.map((s) => <option key={s} value={s} />)}</datalist>
        </div>
        <div><label className={labelCls}>District</label>
          <input list={`${testPrefix}-districts`} value={value.district || ''} onChange={(e) => onDistrict(e.target.value)} className={inputCls} placeholder="e.g. Pune" data-testid={`${testPrefix}-district-input`} />
          <datalist id={`${testPrefix}-districts`}>{districtSugg.map((d) => <option key={d} value={d} />)}</datalist>
        </div>
        <div><label className={labelCls}>City / Town / Village *</label>
          <input list={`${testPrefix}-cities`} value={value.city || ''} onChange={(e) => onCity(e.target.value)} className={inputCls} placeholder="e.g. Baramati" data-testid={`${testPrefix}-city-input`} />
          <datalist id={`${testPrefix}-cities`}>{uniq(citySugg.map((o) => o.Name)).map((n) => <option key={n} value={n}>{citySugg.find((o) => o.Name === n)?.District}</option>)}</datalist>
        </div>
        <div><label className={labelCls}>Area / Locality *</label><input value={value.area || ''} onChange={(e) => set({ area: e.target.value })} className={inputCls} placeholder="e.g. Station Road" data-testid={`${testPrefix}-area-input`} /></div>
        <div><label className={labelCls}>PIN / Post Code</label><input value={value.pincode || ''} onChange={(e) => onPin(e.target.value)} className={inputCls} placeholder="e.g. 413102" inputMode="numeric" data-testid={`${testPrefix}-pincode-input`} /></div>
      </div>
      <div><label className={labelCls}>Address / Map Location (optional)</label><input value={value.address || ''} onChange={(e) => set({ address: e.target.value })} className={inputCls} placeholder="Shop no, street, landmark or Google Maps link" maxLength={300} data-testid={`${testPrefix}-address-input`} /></div>
    </div>
  );
};
